/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart3,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  Download,
  Eye,
  Search,
  UserRound,
  X,
  Package,
  ShoppingCart,
  TrendingUp,
  IndianRupee,
  Tag,
  CheckCircle2,
  Clock,
  Layers,
  Users
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import API, { IMAGE_BASE_URL } from '../services/api';
import './Reports.css';
import './Dashboard.css';

const LIMIT = 10;
const REPORTS = [
  { key: 'products', label: 'Product Report', icon: BarChart3, endpoint: '/admin/reports/products', exportEndpoint: '/admin/reports/products/export' },
  { key: 'customers', label: 'Customer Report', icon: UserRound, endpoint: '/admin/reports/customers', exportEndpoint: '/admin/reports/customers/export' },
  { key: 'orders', label: 'Order Report', icon: ClipboardList, endpoint: '/admin/reports/orders', exportEndpoint: '/admin/reports/orders/export' },
  { key: 'payments', label: 'Payment Report', icon: CreditCard, endpoint: '/admin/reports/payments', exportEndpoint: '/admin/reports/payments/export' },
];
const DEFAULT_FILTERS = REPORTS.reduce((acc, item) => ({ ...acc, [item.key]: { search: '', fromDate: '', toDate: '' } }), {});

function cloneFilters() { return JSON.parse(JSON.stringify(DEFAULT_FILTERS)); }
function money(value) { return '\u20B9' + Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
function count(value) { return Number(value || 0).toLocaleString('en-IN'); }
function text(value) { return value === undefined || value === null || value === '' ? '\u2014' : String(value); }
function dateValue(date) { const d = new Date(date); return Number.isNaN(d.getTime()) ? '' : d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
function toDateObject(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes('T') ? raw : raw.length === 10 ? raw + 'T12:00:00' : raw;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}
function dateText(value) {
  const d = toDateObject(value);
  if (!d) return '\u2014';
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }).format(d);
}
function dateTimeText(value) {
  const d = toDateObject(value);
  if (!d) return '\u2014';
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).format(d) + ' IST';
}
function exportDateTime(value) {
  const d = toDateObject(value);
  if (!d) return value;
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: 'Asia/Kolkata'
  }).formatToParts(d).reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute} ${parts.dayPeriod}`;
}
function customerDateText(value) { if (!value) return '\u2014'; return String(value).length === 10 ? dateText(value) : dateTimeText(value); }
function rootOf(res) { const raw = res && res.data; if (!raw) return {}; if (Array.isArray(raw)) return { rows: raw }; if (Array.isArray(raw.data)) return { ...raw, rows: raw.data }; if (raw.data && typeof raw.data === 'object') return raw.data; return raw; }
function rowsOf(res, type) { const root = rootOf(res); const keys = { products: ['data', 'products', 'productReport', 'productSales'], customers: ['data', 'customers', 'customerReport'], orders: ['data', 'orders', 'orderReport'], payments: ['data', 'payments', 'paymentReport'] }[type]; for (const key of keys) if (Array.isArray(root[key])) return root[key].filter((row) => row && typeof row === 'object'); if (Array.isArray(root.rows)) return root.rows.filter((row) => row && typeof row === 'object'); return []; }
function pageMeta(res, rows, fallbackPage) { const root = rootOf(res); const meta = (res && res.data && res.data.pagination) || root.pagination || root.meta || {}; const total = Number(meta.total || meta.totalRecords || root.total || rows.length || 0); const limit = Number(meta.limit || LIMIT); return { page: Number(meta.page || fallbackPage || 1), limit, total, totalPages: Number(meta.totalPages || Math.ceil(total / limit) || 0) }; }
function params(filter, page) { const out = { page, limit: LIMIT }; Object.entries(filter || {}).forEach(([key, value]) => { if (value !== undefined && value !== null && value !== '') out[key] = value; }); return out; }
function resolveImageUrl(value) {
  if (!value) return '';
  let raw = value;
  if (Array.isArray(raw)) raw = raw[0];
  if (raw && typeof raw === 'object') raw = raw.url || raw.path || raw.filename || raw.src || raw.image || raw.secure_url || '';
  if (!raw) return '';
  const cleaned = String(raw).trim().replace(/\\/g, '/');
  if (!cleaned) return '';
  if (/^(https?:)?\/\//i.test(cleaned) || cleaned.startsWith('data:') || cleaned.startsWith('blob:')) return cleaned;
  const base = String(IMAGE_BASE_URL || '').replace(/\/+$/, '');
  return base + '/' + cleaned.replace(/^\/+/, '');
}
function imageSrc(row) {
  const candidates = [
    row && row.productImage,
    row && row.image,
    row && row.imageUrl,
    row && row.image_url,
    row && row.imageURL,
    row && row.thumbnail,
    row && row.productImg,
    row && row.photo,
    row && row.picture,
    row && row.cover,
    row && row.url,
    row && row.images,
    row && row.photos,
    row && row.pictures,
    row && row.thumbnails,
  ];
  for (const candidate of candidates) {
    const src = resolveImageUrl(candidate);
    if (src) return src;
  }
  return '';
}
function directValue(row, keys) {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    const value = key.includes('.') ? key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), row) : row[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
}
function productName(row) { return text(directValue(row, ['productName', 'name', 'product']) || (row && row.product && row.product.name)); }
function categoryName(row) { return text(directValue(row, ['categoryName']) || (row && row.category && (row.category.name || row.category.categoryName)) || (row && row.category)); }
function subcategoryName(row) { return text(directValue(row, ['subcategoryName']) || (row && row.subcategory && (row.subcategory.name || row.subcategory.subcategoryName)) || (row && row.subcategory)); }
function customerName(row) { return text(directValue(row, ['customerName', 'name']) || (row && row.customer && row.customer.name)); }
function customerPhone(row) { return text(directValue(row, ['phone', 'customerPhone']) || (row && row.customer && row.customer.phone)); }
function orderId(row) { return text(directValue(row, ['orderId', 'orderID', 'order_id', 'order._id', 'order.id', '_id', 'id'])); }
function paymentOrderId(row) { return text(directValue(row, ['orderId', 'orderID', 'order_id', 'order._id', 'order.id', 'order'])); }
function orderNumber(row) { return text(directValue(row, ['orderNumber', 'orderNo', 'order_no', 'orderNumberValue', 'number'])); }
function productLine(item) { if (!item) return ''; if (typeof item !== 'object') return text(item); const productObj = item.product || item.productDetails || item.item || {}; const name = directValue(item, ['productName', 'name', 'title']) || directValue(productObj, ['productName', 'name', 'title']) || directValue(item, ['productId', 'product']) || directValue(productObj, ['productId', 'id']) || 'Product'; const qty = item.quantity ?? item.qty ?? item.totalQuantity ?? productObj.quantity ?? productObj.qty ?? productObj.totalQuantity; const price = item.total ?? item.totalPrice ?? item.price ?? item.productPrice ?? productObj.total ?? productObj.totalPrice ?? productObj.price ?? productObj.productPrice; return [text(name), qty ? 'Qty: ' + count(qty) : '', price !== undefined && price !== null ? money(price) : ''].filter(Boolean).join(' - '); }
function productsText(row) {
  const list = row && (row.products || row.items || row.orderItems || row.productNames || row.productDetails || row.lineItems);
  if (Array.isArray(list)) return list.map(productLine).filter(Boolean).join(', ') || '\u2014';
  if (list && typeof list === 'object') {
    const values = Array.isArray(list.items) ? list.items : Object.values(list);
    return values.map(productLine).filter(Boolean).join(', ') || '\u2014';
  }
  return text(directValue(row, ['products', 'productName', 'product', 'items']));
}
function paymentId(row) {
  return text(directValue(row, [
    'paymentId', 'paymentID', 'payment_id',
    'payment.paymentId', 'payment.paymentID', 'payment.payment_id', 'payment.id',
    'transactionId', 'transactionID', 'transaction_id',
    'razorpayPaymentId', 'razorpay_payment_id', 'gatewayTransactionId', 'txnId'
  ]));
}
function numericValue(row, keys) {
  const value = keys.map((key) => row && row[key]).find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
  if (value === undefined || value === null || value === '') return 0;
  if (typeof value === 'number') return value;
  const parsed = Number(String(value).replace(/[₹,\s]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}
function discountValue(row) {
  const direct = numericValue(row, ['totalDiscount', 'discount', 'discountAmount']);
  if (direct) return direct;
  return numericValue(row, ['couponDiscount', 'couponDiscountAmount']) + numericValue(row, ['superCoinsDiscount', 'superCoinDiscount', 'superCoinsAmount', 'superCoinAmount']) + numericValue(row, ['otherDiscount', 'otherDiscountAmount']);
}
function searchableText(value) { if (value === undefined || value === null) return ''; if (Array.isArray(value)) return value.map(searchableText).join(' '); if (typeof value === 'object') return Object.keys(value).concat(Object.values(value)).map(searchableText).join(' '); return String(value); }
function statusBadge(value) { return <span className={'rp-status rp-status-' + String(value || '').toLowerCase()}>{text(value)}</span>; }
function downloadBlob(filename, blob) { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); }
function parseCsvLine(line) {
  const values = []; let value = ''; let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"' && line[index + 1] === '"' && quoted) { value += '"'; index += 1; }
    else if (char === '"') quoted = !quoted;
    else if (char === ',' && !quoted) { values.push(value); value = ''; }
    else value += char;
  }
  values.push(value);
  return values;
}
async function downloadReportBlob(filename, blob, removedHeaders = []) {
  const content = await blob.text();
  if (!content || content.slice(0, 2) === 'PK') { downloadBlob(filename, blob); return; }
  const lines = content.split(/\r?\n/);
  if (!lines.length) { downloadBlob(filename, blob); return; }
  const headers = parseCsvLine(lines[0]);
  const removedIndexes = headers.reduce((indexes, header, index) => {
    if (removedHeaders.includes(header.trim().toLowerCase())) indexes.push(index);
    return indexes;
  }, []);
  const dateIndexes = headers.reduce((indexes, header, index) => {
    const normalized = header.trim().toLowerCase();
    if (normalized.includes('date') || normalized.includes('created at') || normalized.includes('last order')) indexes.push(index);
    return indexes;
  }, []);
  if (!removedIndexes.length && !dateIndexes.length) { downloadBlob(filename, blob); return; }
  const output = lines.map((line, lineIndex) => {
    if (!line) return line;
    return parseCsvLine(line).map((value, index) => {
      if (lineIndex > 0 && dateIndexes.includes(index)) return exportDateTime(value);
      return value;
    }).filter((_, index) => !removedIndexes.includes(index)).map((value) => '"' + value.replace(/"/g, '""') + '"').join(',');
  }).join('\n');
  downloadBlob(filename, new Blob([output], { type: 'text/csv;charset=utf-8;' }));
}
function csvFallback(filename, rows, columns) { const data = rows.length ? rows : [{}]; const cols = columns.filter((col) => col.key !== 'action'); const lines = [cols.map((col) => col.label).join(',')].concat(data.map((row, index) => cols.map((col) => '"' + String((col.key === 'sno' ? index + 1 : col.export ? col.export(row) : row[col.key]) ?? '').replace(/"/g, '""') + '"').join(','))); const headers = cols.map((col) => col.label); const output = lines.map((line, lineIndex) => { if (lineIndex === 0) return line; return parseCsvLine(line).map((value, index) => headers[index] && (headers[index].toLowerCase().includes('date') || headers[index].toLowerCase().includes('last order')) ? exportDateTime(value) : value).map((value) => '"' + value.replace(/"/g, '""') + '"').join(','); }).join('\n'); downloadBlob(filename, new Blob([output], { type: 'text/csv;charset=utf-8;' })); }
function humanLabel(key) { return text(String(key).replace(/^_+/, '').replace(/Id$/, ' ID').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')).replace(/\b\w/g, (char) => char.toUpperCase()); }
function detailValue(key, value) { if (value === undefined || value === null || value === '') return '\u2014'; if (/date|createdAt|updatedAt|lastOrder|firstOrder|paymentDate|orderDate/i.test(key)) return dateTimeText(value); if (/amount|price|sales|spent|discount|revenue|finalAmount|productsTotal|subtotal/i.test(key) && typeof value !== 'string') return money(value); if (typeof value === 'number') return count(value); if (Array.isArray(value)) return value.length ? value.map(productLine).filter(Boolean).join(' | ') : '\u2014'; if (value && typeof value === 'object') return value.name || value.title || value.productName || value.orderNumber || value._id || JSON.stringify(value); return text(value); }
function extraDetails(row, used) { return Object.entries(row || {}).filter(([key, value]) => !key.startsWith('_') && !['__v', 'isDeleted'].includes(key) && value !== undefined && value !== null && value !== '' && !used.has(key)).map(([key, value]) => ({ label: humanLabel(key), value: detailValue(key, value) })); }
function section(title, items) { return { title, items: items.filter((item) => item && item.value !== undefined && item.value !== null && item.value !== '') }; }
function orderItems(row) { const items = row && (row.products || row.items || row.orderItems || row.order_items); return Array.isArray(items) ? items : []; }
function relatedProduct(item) { if (!item || typeof item !== 'object') return {}; if (item.product && typeof item.product === 'object') return item.product; if (item.productId && typeof item.productId === 'object') return item.productId; return item.productDetails || item.product_info || item.product_data || {}; }
function orderField(row, keys) { return directValue(row, keys); }
function optionalMoney(value) { return value === undefined || value === null || value === '' ? '' : money(value); }
function orderCoupon(row) { const coupon = row && (row.coupon || row.appliedCoupon || row.couponDetails); return coupon && typeof coupon === 'object' ? coupon : {}; }
function orderCouponCode(row) { const coupon = orderCoupon(row); const value = orderField(row, ['couponCode', 'coupon_code']) || coupon.couponCode || coupon.code; return value || (typeof row?.coupon === 'string' ? row.coupon : ''); }
function orderCouponDiscount(row) { const coupon = orderCoupon(row); return orderField(row, ['couponDiscount', 'couponDiscountAmount']) ?? coupon.discountAmount ?? coupon.couponDiscount ?? coupon.amount; }
function orderItemPrice(item) { const product = relatedProduct(item); return (item && (item.price ?? item.unitPrice ?? item.sellingPrice)) ?? product.price ?? product.sellingPrice ?? product.salePrice ?? (item && (item.totalPrice ?? item.total)); }
function orderItemQuantity(item) { return (item && (item.quantity ?? item.qty ?? item.totalQuantity)) ?? 1; }
function orderSubtotal(row) { const direct = orderField(row, ['subtotal', 'subTotal', 'subtotalAmount', 'productsTotal', 'itemsTotal']); if (direct !== '') return direct; return orderItems(row).reduce((sum, item) => sum + (Number(orderItemPrice(item)) || 0) * (Number(orderItemQuantity(item)) || 1), 0); }
function makeOrderDetailPayload(row) {
  const coupon = orderCoupon(row);
  const coins = row && row.superCoins && typeof row.superCoins === 'object' ? row.superCoins : {};
  const gift = row && row.giftPacking && typeof row.giftPacking === 'object' ? row.giftPacking : {};
  const coinsUsed = orderField(row, ['superCoinsUsed', 'superCoinUsed', 'coinsUsed']) ?? coins.used ?? coins.coins ?? coins.count;
  const coinsDiscount = orderField(row, ['superCoinsDiscount', 'superCoinsAmount', 'superCoinDiscount', 'superCoinAmount', 'coinDiscount']) ?? coins.discount ?? coins.discountAmount ?? coins.amount ?? coins.value;
  const giftSelected = orderField(row, ['isGiftPacking', 'giftWrap', 'isGiftWrapped']) ?? gift.enabled ?? gift.isEnabled ?? gift.selected;
  const giftAmount = orderField(row, ['giftPackingAmount', 'giftPackAmount', 'giftWrapAmount']) ?? gift.amount ?? gift.price ?? gift.cost;
  const couponDiscount = orderCouponDiscount(row);
  const couponType = coupon.discountType ?? coupon.type ?? orderField(row, ['couponDiscountType', 'discountType']);
  const couponValue = coupon.discountValue ?? coupon.value ?? coupon.discount ?? coupon.percentage ?? orderField(row, ['couponDiscountValue', 'discountValue', 'discountPercentage']);
  const shipping = orderField(row, ['shippingCharges', 'shippingCharge', 'shipping_charges', 'shippingFee', 'shippingCost', 'deliveryCharge', 'deliveryCharges', 'deliveryFee', 'shipCharge']);
  const otherDiscount = orderField(row, ['otherDiscount', 'otherDiscountAmount']);
  const totalDiscount = orderField(row, ['totalDiscount', 'discount', 'discountAmount']);
  const finalAmount = orderField(row, ['finalAmount', 'payableAmount', 'totalPayable', 'grandTotal', 'total', 'amount']);
  const items = orderItems(row);
  const productLines = items.map((item) => { const product = relatedProduct(item); const name = item.productName || item.name || item.title || product.productName || product.name || product.title || item.productId || item.product || 'Product'; const quantity = orderItemQuantity(item); const price = orderItemPrice(item); return [text(name), 'Qty: ' + count(quantity), optionalMoney(price)].filter(Boolean).join(' - '); }).filter(Boolean).join(', ');
  const hasDiscountData = totalDiscount !== '' || couponDiscount !== undefined || coinsDiscount !== undefined || otherDiscount !== '';
  const discountTotal = hasDiscountData ? (totalDiscount !== '' ? totalDiscount : (Number(couponDiscount || 0) + Number(coinsDiscount || 0) + Number(otherDiscount || 0))) : '';
  const used = new Set(['orderId', '_id', 'id', 'orderNumber', 'customerId', 'customerName', 'customer', 'orderDate', 'createdAt', 'products', 'items', 'orderItems', 'order_items', 'totalItems', 'itemsCount', 'totalQuantity', 'quantity', 'productsSold', 'productsTotal', 'subtotal', 'subTotal', 'subtotalAmount', 'itemsTotal', 'totalAmount', 'discount', 'totalDiscount', 'discountAmount', 'couponCode', 'coupon_code', 'coupon', 'appliedCoupon', 'couponDetails', 'couponDiscount', 'couponDiscountAmount', 'couponDiscountType', 'couponDiscountValue', 'discountType', 'discountValue', 'discountPercentage', 'superCoins', 'superCoinsUsed', 'superCoinUsed', 'coinsUsed', 'superCoinsDiscount', 'superCoinsAmount', 'superCoinDiscount', 'superCoinAmount', 'coinDiscount', 'giftPacking', 'isGiftPacking', 'giftWrap', 'isGiftWrapped', 'giftPackingAmount', 'giftPackAmount', 'giftWrapAmount', 'otherDiscount', 'otherDiscountAmount', 'shippingCharges', 'shippingCharge', 'shipping_charges', 'deliveryCharge', 'deliveryCharges', 'shipCharge', 'finalAmount', 'payableAmount', 'totalPayable', 'grandTotal', 'total', 'amount', 'paymentMethod', 'paymentStatus', 'payment', 'orderStatus', 'status']);
  return { title: 'Order Details', sections: [
    section('Order Summary', [
      { label: 'Order ID', value: orderId(row) }, { label: 'Customer', value: customerName(row) }, { label: 'Customer ID', value: text(row.customerId) }, { label: 'Order Date', value: dateTimeText(row.orderDate || row.createdAt) },
      { label: 'Products / Items', value: productLines }, { label: 'Items', value: count(row.totalItems || row.itemsCount || items.length) }, { label: 'Quantity', value: count(row.totalQuantity || row.quantity || items.reduce((sum, item) => sum + Number(orderItemQuantity(item) || 0), 0)) }, { label: 'Subtotal', value: optionalMoney(orderSubtotal(row)) }, { label: 'Coupon Code', value: text(orderCouponCode(row)) }, { label: 'Coupon Discount Type', value: text(couponType) }, { label: 'Coupon Discount Value', value: couponValue === undefined || couponValue === null || couponValue === '' ? '' : String(couponValue) + (String(couponType || '').toLowerCase().includes('percent') ? '%' : '') }, { label: 'Coupon Discount Amount', value: optionalMoney(couponDiscount) }, { label: 'ISHMAI Coins Used', value: coinsUsed === undefined || coinsUsed === null || coinsUsed === '' ? '' : count(coinsUsed) }, { label: 'ISHMAI Coins Discount', value: optionalMoney(coinsDiscount) }, { label: 'Gift Packing Selected', value: giftSelected === undefined && giftAmount === undefined ? '' : (giftSelected === true || giftSelected === 'true' || giftSelected === 'yes' || giftSelected === 'Yes' ? 'Yes' : 'No') }, { label: 'Gift Packing Amount', value: optionalMoney(giftAmount) }, { label: 'Other Discount', value: optionalMoney(otherDiscount) }, { label: 'Total Discount', value: optionalMoney(discountTotal) }, { label: 'Shipping Charges', value: optionalMoney(shipping) }, { label: 'Final Payable Amount', value: optionalMoney(finalAmount) }, { label: 'Payment Method', value: text(row.paymentMethod || row.payment) }, { label: 'Payment Status', value: text(row.paymentStatus || row.status) }, { label: 'Order Status', value: text(row.orderStatus || row.status) }
    ]), section('Additional API Data', extraDetails(row, used))
  ] };
}
function makeDetailPayload(type, row, detail) {
  const customer = (detail && detail.customer) || row || {}; const summary = (detail && detail.summary) || row || {}; const history = (detail && detail.orderHistory) || row.orderHistory || [];
  if (type === 'orders') return makeOrderDetailPayload(row);
  if (type === 'products') { const used = new Set(['productId', 'productName', 'name', 'product', 'productImage', 'image', 'imageUrl', 'thumbnail', 'images', 'categoryId', 'categoryName', 'category', 'subcategoryId', 'subcategoryName', 'subcategory', 'sellingPrice', 'price', 'productPrice', 'unitsSold', 'quantitySold', 'quantity', 'totalOrders', 'orders', 'orderCount', 'totalSales', 'sales', 'revenue']); return { title: 'Product Details', image: imageSrc(row), sections: [section('Product Summary', [{ label: 'Product Name', value: productName(row) }, { label: 'Product Image', value: imageSrc(row) ? 'Available' : '\u2014' }, { label: 'Category', value: categoryName(row) }, { label: 'Subcategory', value: subcategoryName(row) }, { label: 'Price', value: money(row.sellingPrice || row.price || row.productPrice) }, { label: 'Units Sold', value: count(row.unitsSold || row.quantitySold || row.quantity) }, { label: 'Orders', value: count(row.totalOrders || row.orders || row.orderCount) }, { label: 'Total Sales', value: money(row.totalSales || row.sales || row.revenue) }]), section('Additional API Data', extraDetails(row, used))] }; }
  if (type === 'customers') { const used = new Set(['customerId', '_id', 'id', 'name', 'customerName', 'customer', 'email', 'phone', 'customerPhone', 'totalOrders', 'orders', 'orderCount', 'totalProductsPurchased', 'productsPurchased', 'totalQuantity', 'totalSpent', 'spent', 'finalAmount', 'lastOrderDate', 'orderDate', 'createdAt', 'orderHistory']); return { title: 'Customer Details', sections: [section('Customer Summary', [{ label: 'Customer Name', value: text(customer.name || row.customerName || row.name) }, { label: 'Customer ID', value: text(customer.customerId || row.customerId || row._id || row.id) }, { label: 'Phone', value: text(customer.phone || row.customerPhone || row.phone) }, { label: 'Email', value: text(customer.email || row.email) }, { label: 'Total Orders', value: count(summary.totalOrders || row.totalOrders || row.orders || row.orderCount) }, { label: 'Products Purchased', value: count(summary.totalProductsPurchased || row.totalProductsPurchased || row.productsPurchased || row.totalQuantity) }, { label: 'Total Spent', value: money(summary.totalSpent || row.totalSpent || row.spent || row.finalAmount) }, { label: 'First Order', value: customerDateText(summary.firstOrderDate) }, { label: 'Last Order', value: customerDateText(summary.lastOrderDate || row.lastOrderDate || row.orderDate || row.createdAt) }]), section('Order / Purchase History', Array.isArray(history) ? history.map((order, index) => ({ label: order.orderNumber || order.orderId || 'Order ' + (index + 1), value: [customerDateText(order.orderDate), productsText(order), 'Qty: ' + count(order.totalQuantity), money(order.finalAmount)].filter(Boolean).join(' | ') })) : []), section('Additional API Data', extraDetails(row, used))] }; }
  if (type === 'orders') { const used = new Set(['orderId', '_id', 'id', 'orderNumber', 'customerId', 'customerName', 'customer', 'orderDate', 'createdAt', 'products', 'items', 'orderItems', 'totalItems', 'itemsCount', 'totalQuantity', 'quantity', 'productsSold', 'productsTotal', 'subtotal', 'totalAmount', 'discount', 'totalDiscount', 'discountAmount', 'couponCode', 'coupon', 'couponDiscount', 'couponDiscountAmount', 'superCoinsDiscount', 'superCoinDiscount', 'otherDiscount', 'finalAmount', 'total', 'amount', 'paymentMethod', 'paymentStatus', 'payment', 'orderStatus', 'status']); return { title: 'Order Details', sections: [section('Order Summary', [{ label: 'Order ID', value: orderId(row) }, { label: 'Customer', value: customerName(row) }, { label: 'Customer ID', value: text(row.customerId) }, { label: 'Order Date', value: dateText(row.orderDate || row.createdAt) }, { label: 'Products / Items', value: productsText(row) }, { label: 'Items', value: count(row.totalItems || row.itemsCount || (Array.isArray(row.products) ? row.products.length : 0)) }, { label: 'Quantity', value: count(row.totalQuantity || row.quantity || row.productsSold) }, { label: 'Products Total', value: money(row.productsTotal || row.subtotal || row.totalAmount) }, { label: 'Discount', value: money(discountValue(row)) }, { label: 'Coupon Code', value: text(row.couponCode || (row.coupon && (row.coupon.code || row.coupon.couponCode))) }, { label: 'Coupon Discount', value: money(numericValue(row, ['couponDiscount', 'couponDiscountAmount'])) }, { label: 'ISHMAI Coins Discount', value: money(numericValue(row, ['superCoinsDiscount', 'superCoinDiscount', 'superCoinsAmount'])) }, { label: 'Other Discount', value: money(numericValue(row, ['otherDiscount', 'otherDiscountAmount'])) }, { label: 'Final Amount', value: money(row.finalAmount || row.total || row.amount) }, { label: 'Payment Method', value: text(row.paymentMethod) }, { label: 'Payment Status', value: text(row.paymentStatus) }, { label: 'Order Status', value: text(row.orderStatus || row.status) }]), section('Additional API Data', extraDetails(row, used))] }; }
  const used = new Set(['paymentId', '_id', 'id', 'orderId', 'orderNumber', 'customerId', 'customerName', 'customer', 'customerEmail', 'customerPhone', 'products', 'items', 'orderItems', 'paymentMethod', 'method', 'paymentStatus', 'status', 'transactionId', 'transactionID', 'razorpayPaymentId', 'paymentAmount', 'amount', 'finalAmount', 'couponDiscount', 'couponDiscountAmount', 'paymentDate', 'createdAt', 'orderDate']); return { title: 'Payment Details', sections: [section('Payment Summary', [{ label: 'Payment ID', value: paymentId(row) }, { label: 'Customer', value: customerName(row) }, { label: 'Customer ID', value: text(row.customerId) }, { label: 'Customer Phone', value: text(row.customerPhone) }, { label: 'Customer Email', value: text(row.customerEmail) }, { label: 'Order ID', value: paymentOrderId(row) }, { label: 'Products', value: productsText(row) }, { label: 'Payment Method', value: text(row.paymentMethod || row.method) }, { label: 'Payment Status', value: text(row.paymentStatus || row.status) }, { label: 'Transaction ID', value: text(row.transactionId || row.transactionID || row.razorpayPaymentId) }, { label: 'Amount', value: money(row.paymentAmount || row.amount || row.finalAmount) }, { label: 'Coupon Discount Amount', value: money(numericValue(row, ['couponDiscountAmount', 'couponDiscount'])) }, { label: 'Payment Date', value: dateText(row.paymentDate || row.createdAt || row.orderDate) }]), section('Additional API Data', extraDetails(row, used))] };
}

function SearchField({ value, onChange }) { return <label className="rp-search-field"><Search size={17} /><input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Search report" /></label>; }
function ReportImage({ row, alt }) {
  const src = imageSrc(row || {});
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (!src || failed) return <span className="rp-img-empty">\u2014</span>;
  return <img className="rp-product-img" src={src} alt={alt || 'Product'} loading="lazy" onError={() => setFailed(true)} />;
}
function ViewButton({ onClick }) { return <button type="button" className="rp-action-btn" onClick={onClick}><Eye size={15} />View</button>; }
function DataTable({ section, columns, rows, loading, page }) { const start = ((page || 1) - 1) * LIMIT; return <div className="rp-table-wrap"><table className={'rp-table rp-table-' + section}><thead><tr>{columns.map((col) => <th key={col.key} className={col.align ? 'rp-align-' + col.align : ''}>{col.label}</th>)}</tr></thead><tbody>{loading && <tr><td colSpan={columns.length} className="rp-table-state">Loading...</td></tr>}{!loading && rows.length === 0 && <tr><td colSpan={columns.length} className="rp-table-state">No data available</td></tr>}{!loading && rows.map((row, index) => <tr key={row._rowKey || row._id || row.id || row.orderId || row.paymentId || row.customerId || row.productId || index}>{columns.map((col) => <td key={col.key} className={col.align ? 'rp-align-' + col.align : ''}>{col.key === 'sno' ? start + index + 1 : col.render ? col.render(row) : row[col.key]}</td>)}</tr>)}</tbody></table></div>; }
function Pagination({ page, total, onChange }) { const totalPages = Math.max(1, Math.ceil((total || 0) / LIMIT)); const start = total ? ((page - 1) * LIMIT) + 1 : 0; const end = Math.min(total || 0, page * LIMIT); const nums = Array.from({ length: Math.min(totalPages, 3) }, (_, i) => Math.max(1, Math.min(totalPages - 2, page - 1)) + i).filter((p, i, arr) => p <= totalPages && arr.indexOf(p) === i); return <div className="rp-pagination"><span>Showing {count(start)} to {count(end)} of {count(total)} entries</span><div className="rp-page-buttons"><button type="button" className="rp-page-btn" disabled={page <= 1} onClick={() => onChange(page - 1)}><ChevronLeft size={16} /></button>{nums.map((p) => <button type="button" key={p} className={'rp-page-number ' + (p === page ? 'is-active' : '')} onClick={() => onChange(p)}>{p}</button>)}<button type="button" className="rp-page-btn" disabled={page >= totalPages} onClick={() => onChange(page + 1)}><ChevronRight size={16} /></button></div></div>; }
function DetailModal({ modal, onClose }) { if (!modal) return null; return <div className="rp-modal-backdrop" onClick={onClose}><div className="rp-cust-modal rp-detail-modal" onClick={(e) => e.stopPropagation()}><button type="button" className="rp-modal-close" onClick={onClose}><X size={18} /></button><h3>{modal.title}</h3>{modal.image && <div className="rp-detail-image-row"><img src={modal.image} alt={modal.title} /></div>}{modal.loading && <div className="rp-table-state">Loading details...</div>}<div className="rp-detail-sections">{(modal.sections || []).filter((group) => group.items && group.items.length).map((group) => <section key={group.title} className="rp-detail-section"><h4>{group.title}</h4><div className="rp-detail-grid">{group.items.map((item, index) => <div key={item.label + index}><span>{item.label}</span><strong>{item.value || '\u2014'}</strong></div>)}</div></section>)}</div></div></div>; }

function getReportStats(type, rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const displayCount = safeRows.length;

  if (type === 'products') {
    const totalUnits = safeRows.reduce((sum, r) => sum + Number(r.unitsSold || r.quantitySold || r.quantity || 0), 0);
    const totalOrders = safeRows.reduce((sum, r) => sum + Number(r.totalOrders || r.orders || r.orderCount || 0), 0);
    const totalSales = safeRows.reduce((sum, r) => sum + Number(r.totalSales || r.sales || r.revenue || 0), 0);

    return [
      {
        key: 'prod-total',
        targetReport: 'products',
        color: 'blue',
        icon: Package,
        label: 'Total Products',
        value: count(displayCount),
        desc: 'Products in view'
      },
      {
        key: 'prod-units',
        targetReport: 'products',
        color: 'green',
        icon: ShoppingCart,
        label: 'Units Sold',
        value: count(totalUnits),
        desc: 'Total units purchased'
      },
      {
        key: 'prod-orders',
        targetReport: 'products',
        color: 'orange',
        icon: ClipboardList,
        label: 'Orders Count',
        value: count(totalOrders),
        desc: 'Total orders placed'
      },
      {
        key: 'prod-revenue',
        targetReport: 'products',
        color: 'purple',
        icon: IndianRupee,
        label: 'Total Sales',
        value: money(totalSales),
        desc: 'Total gross revenue'
      }
    ];
  }

  if (type === 'customers') {
    const totalOrders = safeRows.reduce((sum, r) => sum + Number(r.totalOrders || r.orders || r.orderCount || 0), 0);
    const totalPurchased = safeRows.reduce((sum, r) => sum + Number(r.totalProductsPurchased || r.productsPurchased || r.totalQuantity || 0), 0);
    const totalSpent = safeRows.reduce((sum, r) => sum + Number(r.totalSpent || r.spent || r.finalAmount || 0), 0);

    return [
      {
        key: 'cust-total',
        targetReport: 'customers',
        color: 'blue',
        icon: UserRound,
        label: 'Total Customers',
        value: count(displayCount),
        desc: 'Customers in view'
      },
      {
        key: 'cust-orders',
        targetReport: 'customers',
        color: 'green',
        icon: ClipboardList,
        label: 'Total Orders',
        value: count(totalOrders),
        desc: 'Orders by customers'
      },
      {
        key: 'cust-qty',
        targetReport: 'customers',
        color: 'orange',
        icon: ShoppingCart,
        label: 'Products Bought',
        value: count(totalPurchased),
        desc: 'Total items purchased'
      },
      {
        key: 'cust-spent',
        targetReport: 'customers',
        color: 'purple',
        icon: IndianRupee,
        label: 'Total Spent',
        value: money(totalSpent),
        desc: 'Total customer spend'
      }
    ];
  }

  if (type === 'orders') {
    const totalQty = safeRows.reduce((sum, r) => sum + Number(r.totalQuantity || r.quantity || r.productsSold || r.totalItems || 0), 0);
    const totalDiscount = safeRows.reduce((sum, r) => sum + Number(r.discount || r.totalDiscount || r.couponDiscount || 0), 0);
    const totalAmount = safeRows.reduce((sum, r) => sum + Number(r.finalAmount || r.total || r.amount || 0), 0);

    return [
      {
        key: 'ord-total',
        targetReport: 'orders',
        color: 'blue',
        icon: ClipboardList,
        label: 'Total Orders',
        value: count(displayCount),
        desc: 'Orders in view'
      },
      {
        key: 'ord-qty',
        targetReport: 'orders',
        color: 'green',
        icon: Package,
        label: 'Total Quantity',
        value: count(totalQty),
        desc: 'Items in orders'
      },
      {
        key: 'ord-disc',
        targetReport: 'orders',
        color: 'orange',
        icon: Tag,
        label: 'Total Discounts',
        value: money(totalDiscount),
        desc: 'Discounts applied'
      },
      {
        key: 'ord-revenue',
        targetReport: 'orders',
        color: 'purple',
        icon: IndianRupee,
        label: 'Total Order Value',
        value: money(totalAmount),
        desc: 'Net order volume'
      }
    ];
  }

  if (type === 'payments') {
    const isPaid = (r) => ['success', 'completed', 'paid', 'captured'].includes(String(r.paymentStatus || r.status || '').toLowerCase());
    const paidCount = safeRows.filter(isPaid).length;
    const pendingCount = safeRows.length - paidCount;
    const totalAmount = safeRows.reduce((sum, r) => sum + Number(r.paymentAmount || r.amount || r.finalAmount || 0), 0);

    return [
      {
        key: 'pay-total',
        targetReport: 'payments',
        color: 'blue',
        icon: CreditCard,
        label: 'Total Transactions',
        value: count(displayCount),
        desc: 'Payments in view'
      },
      {
        key: 'pay-success',
        targetReport: 'payments',
        color: 'green',
        icon: CheckCircle2,
        label: 'Paid / Completed',
        value: count(paidCount),
        desc: 'Successful payments'
      },
      {
        key: 'pay-pending',
        targetReport: 'payments',
        color: 'orange',
        icon: Clock,
        label: 'Pending / Other',
        value: count(pendingCount),
        desc: 'Pending or failed'
      },
      {
        key: 'pay-volume',
        targetReport: 'payments',
        color: 'purple',
        icon: IndianRupee,
        label: 'Total Volume',
        value: money(totalAmount),
        desc: 'Total amount received'
      }
    ];
  }

  return [];
}

function ReportStatsCards({ stats, onSelectReport }) {
  if (!stats || !stats.length) return null;
  return (
    <div className="rp-stats-grid">
      {stats.map((item) => {
        const IconComponent = item.icon;
        const handleClick = () => {
          if (item.targetReport && onSelectReport) {
            onSelectReport(item.targetReport);
          }
        };

        return (
          <div
            key={item.key}
            className={`rp-stat-card rp-stat-card--${item.color}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            title={`View ${item.label}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }}
          >
            <div className="rp-stat-icon-wrap">
              {IconComponent ? <IconComponent size={22} /> : null}
            </div>
            <div className="rp-stat-info">
              <div className="rp-stat-value">{item.value}</div>
              <div className="rp-stat-label">{item.label}</div>
              {item.desc && <div className="rp-stat-desc">{item.desc}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function buildCalendarCells(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);
  return cells;
}

function MiniCalendar({ year, month, start, end, hoverDate, onDayClick, onDayHover, onPrev, onNext, showPrev = true, showNext = true }) {
  const todayStr = dateValue(new Date());
  const cells = buildCalendarCells(year, month);

  const isToday = (d) => d && dateValue(d) === todayStr;
  const isInRange = (d) => {
    if (!d) return false;
    const s = dateValue(d);
    const rangeEnd = end || hoverDate || '';
    if (!start || !rangeEnd) return false;
    const lo = start < rangeEnd ? start : rangeEnd;
    const hi = start < rangeEnd ? rangeEnd : start;
    return s > lo && s < hi;
  };
  const isRangeStart = (d) => {
    if (!d) return false;
    const s = dateValue(d);
    const rangeEnd = end || hoverDate || '';
    if (!start || !rangeEnd) return s === start;
    return s === (start < rangeEnd ? start : rangeEnd);
  };
  const isRangeEnd = (d) => {
    if (!d) return false;
    const s = dateValue(d);
    const rangeEnd = end || hoverDate || '';
    if (!start || !rangeEnd) return false;
    return s === (start < rangeEnd ? rangeEnd : start);
  };

  return (
    <div className="dc-cal">
      <div className="dc-cal-nav">
        {showPrev ? (
          <button type="button" className="dc-cal-nav-btn" onClick={onPrev}>‹</button>
        ) : (
          <div style={{ width: 28 }} />
        )}
        <span className="dc-cal-month-label">{MONTHS[month]} {year}</span>
        {showNext ? (
          <button type="button" className="dc-cal-nav-btn" onClick={onNext}>›</button>
        ) : (
          <div style={{ width: 28 }} />
        )}
      </div>
      <div className="dc-cal-weekdays">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="dc-cal-weekday">{wd}</div>
        ))}
      </div>
      <div className="dc-cal-days">
        {cells.map((cell, idx) => {
          if (!cell) return <div key={'b-' + idx} className="dc-cal-day is-empty" />;
          let cls = 'dc-cal-day';
          if (isToday(cell)) cls += ' is-today';
          if (isRangeStart(cell)) cls += ' is-sel-start';
          if (isRangeEnd(cell)) cls += ' is-sel-end';
          if (isInRange(cell)) cls += ' is-in-range';
          return (
            <button
              key={dateValue(cell)}
              type="button"
              className={cls}
              onClick={() => onDayClick && onDayClick(cell)}
              onMouseEnter={() => onDayHover && onDayHover(dateValue(cell))}
            >
              {cell.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateFilterPopup({ value, onApply, onCancel, onExport, rowsCount }) {
  const todayDate = new Date();
  const todayStr = dateValue(todayDate);

  const [start, setStart] = useState(value.fromDate || todayStr);
  const [end, setEnd] = useState(value.toDate || value.fromDate || todayStr);
  const [preset, setPreset] = useState(value.fromDate || value.toDate ? 'custom' : 'today');
  const [pickStep, setPickStep] = useState('start');
  const [hoverDate, setHoverDate] = useState('');

  const [leftYear, setLeftYear] = useState(todayDate.getFullYear());
  const [leftMonth, setLeftMonth] = useState(todayDate.getMonth() === 0 ? 11 : todayDate.getMonth() - 1);

  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;
  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;

  const goLeft = () => {
    if (leftMonth === 0) {
      setLeftMonth(11);
      setLeftYear((y) => y - 1);
    } else {
      setLeftMonth((m) => m - 1);
    }
  };

  const goRight = () => {
    if (leftMonth === 11) {
      setLeftMonth(0);
      setLeftYear((y) => y + 1);
    } else {
      setLeftMonth((m) => m + 1);
    }
  };

  const applyPreset = (key) => {
    setPreset(key);
    const now = new Date();
    let s = '', e2 = '';

    if (key === 'today') {
      s = todayStr;
      e2 = todayStr;
    } else if (key === 'yesterday') {
      const y = new Date(now);
      y.setDate(now.getDate() - 1);
      s = dateValue(y);
      e2 = dateValue(y);
    } else if (key === '7d') {
      const d7 = new Date(now);
      d7.setDate(now.getDate() - 6);
      s = dateValue(d7);
      e2 = todayStr;
    } else if (key === '30d') {
      const d30 = new Date(now);
      d30.setDate(now.getDate() - 29);
      s = dateValue(d30);
      e2 = todayStr;
    } else if (key === 'this_month') {
      const tm = new Date(now.getFullYear(), now.getMonth(), 1);
      s = dateValue(tm);
      e2 = todayStr;
    } else if (key === 'custom') {
      setStart('');
      setEnd('');
      setPickStep('start');
      return;
    }

    setStart(s);
    setEnd(e2);
    setPickStep('start');
    setHoverDate('');
    onApply({ fromDate: s, toDate: e2 });
  };

  const handleDayClick = (dateObj) => {
    if (!dateObj) return;
    const str = dateValue(dateObj);
    setPreset('custom');
    if (pickStep === 'start' || (start && end)) {
      setStart(str);
      setEnd('');
      setPickStep('end');
    } else {
      if (str < start) {
        setStart(str);
        setEnd(start);
        onApply({ fromDate: str, toDate: start });
      } else {
        setEnd(str);
        onApply({ fromDate: start, toDate: str });
      }
      setPickStep('start');
      setHoverDate('');
    }
  };

  const handleClear = () => {
    setStart('');
    setEnd('');
    setPreset('7d');
    setPickStep('start');
    setHoverDate('');
    onApply({ fromDate: '', toDate: '' });
  };

  const presets = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7d', label: 'Last 7 Days' },
    { key: '30d', label: 'Last 30 Days' },
    { key: 'this_month', label: 'This Month' },
    { key: 'custom', label: 'Custom' },
  ];

  const noData = (start || end) && rowsCount === 0;

  return (
    <div className="dfd-overlay" onClick={onCancel}>
      <div className="dfd-popup" onClick={(e) => e.stopPropagation()}>
        <div className="dfd-header">
          <div>
            <div className="dfd-header-title">Filter & Export</div>
            <div className="dfd-header-sub">Select a date range to filter data</div>
          </div>
          <button type="button" className="dfd-close-btn" onClick={onCancel}>×</button>
        </div>

        <div className="dfd-body">
          <div className="dfd-calendars">
            <div className="dfd-range-display">
              <div className={'dfd-range-field' + (pickStep === 'start' ? ' is-picking' : '')}>
                <div className="dfd-range-field-label">Start Date</div>
                <div className="dfd-range-field-value">{start ? dateText(start) : 'mm/dd/yyyy'}</div>
              </div>
              <div className="dfd-range-sep">
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
              <div className={'dfd-range-field' + (pickStep === 'end' ? ' is-picking' : '')}>
                <div className="dfd-range-field-label">End Date</div>
                <div className="dfd-range-field-value">{end ? dateText(end) : 'mm/dd/yyyy'}</div>
              </div>
            </div>

            <div className="dfd-dual-cal" onMouseLeave={() => setHoverDate('')}>
              <MiniCalendar
                year={leftYear}
                month={leftMonth}
                start={start}
                end={end}
                hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={(s) => { if (pickStep === 'end' && start) setHoverDate(s); }}
                onPrev={goLeft}
                onNext={null}
                showPrev={true}
                showNext={false}
              />
              <div className="dfd-cal-divider" />
              <MiniCalendar
                year={rightYear}
                month={rightMonth}
                start={start}
                end={end}
                hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={(s) => { if (pickStep === 'end' && start) setHoverDate(s); }}
                onPrev={null}
                onNext={goRight}
                showPrev={false}
                showNext={true}
              />
            </div>

            {noData && (
              <div className="dfd-no-data-notice">
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <circle cx={12} cy={12} r={10} />
                  <line x1={12} y1={8} x2={12} y2={12} />
                  <line x1={12} y1={16} x2={12.01} y2={16} />
                </svg>
                No previous data available for this date range
              </div>
            )}
          </div>

          <div className="dfd-presets">
            <div className="dfd-presets-title">Quick Select</div>
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                className={'dfd-preset-btn' + (preset === p.key ? ' is-active' : '')}
                onClick={() => applyPreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="dfd-footer">
          <button
            type="button"
            className="dfd-export-btn"
            onClick={onExport}
          >
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1={12} y1={15} x2={12} y2={3} />
            </svg>
            Download CSV
          </button>
          <div className="dfd-action-btns">
            <button type="button" className="dfd-clear-btn" onClick={handleClear}>
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Reports() {
  const [activeReport, setActiveReport] = useState('products');
  const [draftFilters, setDraftFilters] = useState(cloneFilters());
  const [appliedFilters, setAppliedFilters] = useState(cloneFilters());
  const [pages, setPages] = useState({ products: 1, customers: 1, orders: 1, payments: 1 });
  const [reportData, setReportData] = useState({ products: [], customers: [], orders: [], payments: [] });
  const [meta, setMeta] = useState({ products: { page: 1, total: 0 }, customers: { page: 1, total: 0 }, orders: { page: 1, total: 0 }, payments: { page: 1, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const config = REPORTS.find((item) => item.key === activeReport);
  const activeFilter = draftFilters[activeReport];

  useEffect(() => { let alive = true; setLoading(true); API.get(config.endpoint, { params: params(appliedFilters[activeReport], pages[activeReport]) }).then((res) => { if (!alive) return; const rows = rowsOf(res, activeReport).map((row, index) => ({ ...row, _rowKey: row._id || row.id || row.orderId || row.paymentId || row.customerId || row.productId || index })); setReportData((prev) => ({ ...prev, [activeReport]: rows })); setMeta((prev) => ({ ...prev, [activeReport]: pageMeta(res, rows, pages[activeReport]) })); }).catch(() => { if (alive) { setReportData((prev) => ({ ...prev, [activeReport]: [] })); setMeta((prev) => ({ ...prev, [activeReport]: { page: pages[activeReport], total: 0 } })); } }).finally(() => { if (alive) setLoading(false); }); return () => { alive = false; }; }, [activeReport, pages[activeReport], JSON.stringify(appliedFilters[activeReport])]);
  useEffect(() => { if (dateOpen || detailModal) document.body.classList.add('rp-modal-open'); else document.body.classList.remove('rp-modal-open'); return () => document.body.classList.remove('rp-modal-open'); }, [dateOpen, detailModal]);

  function setDraft(patch) { const next = { ...draftFilters[activeReport], ...patch }; setDraftFilters((prev) => ({ ...prev, [activeReport]: next })); if (Object.prototype.hasOwnProperty.call(patch, 'search')) { setAppliedFilters((prev) => ({ ...prev, [activeReport]: next })); setPages((prev) => ({ ...prev, [activeReport]: 1 })); } }
  function resetFilters() { setDraftFilters((prev) => ({ ...prev, [activeReport]: { ...DEFAULT_FILTERS[activeReport] } })); setAppliedFilters((prev) => ({ ...prev, [activeReport]: { ...DEFAULT_FILTERS[activeReport] } })); setPages((prev) => ({ ...prev, [activeReport]: 1 })); setDateOpen(false); }
  function applyDate(range) { const next = { ...draftFilters[activeReport], ...range }; setDraftFilters((prev) => ({ ...prev, [activeReport]: next })); setAppliedFilters((prev) => ({ ...prev, [activeReport]: next })); setPages((prev) => ({ ...prev, [activeReport]: 1 })); setDateOpen(false); }
  function dateLabel() { const f = activeFilter; if (!f.fromDate && !f.toDate) return 'Date Filter'; return f.fromDate && f.toDate ? dateText(f.fromDate) + ' - ' + dateText(f.toDate) : dateText(f.fromDate || f.toDate); }
  async function openDetails(type, row) { const base = makeDetailPayload(type, row || {}, null); setDetailModal(base); if (type === 'customers' && row && row.customerId) { setDetailModal({ ...base, loading: true }); try { const res = await API.get('/admin/reports/customers/' + row.customerId); setDetailModal(makeDetailPayload(type, row, rootOf(res))); } catch (err) { setDetailModal(base); } } }
  async function exportCurrent() { setExporting(true); try { const exportFilter = activeReport === 'orders' ? { ...appliedFilters[activeReport], detail: 'true' } : appliedFilters[activeReport]; const res = await API.get(config.exportEndpoint, { params: params(exportFilter, pages[activeReport]), responseType: 'blob' }); await downloadReportBlob(activeReport + '-report.csv', res.data, activeReport === 'orders' || activeReport === 'payments' ? ['order number', 'ordernumber', 'order no', 'order_no'] : []); } catch (err) { csvFallback(activeReport + '-report.csv', reportData[activeReport], columns); } finally { setExporting(false); } }

  const columnsByReport = {
    products: [
      { key: 'sno', label: 'S.No', align: 'center' },
      { key: 'image', label: 'Image', align: 'center', render: (row) => <ReportImage row={row} alt={productName(row)} />, export: () => '' },
      { key: 'productName', label: 'Product Name', render: productName, export: productName },
      { key: 'categoryName', label: 'Category', render: categoryName, export: categoryName },
      { key: 'subcategoryName', label: 'Subcategory', render: subcategoryName, export: subcategoryName },
      { key: 'sellingPrice', label: 'Price', align: 'right', render: (row) => money(row.sellingPrice || row.price || row.productPrice), export: (row) => row.sellingPrice || row.price || row.productPrice || 0 },
      { key: 'unitsSold', label: 'Units Sold', align: 'center', render: (row) => count(row.unitsSold || row.quantitySold || row.quantity), export: (row) => row.unitsSold || row.quantitySold || row.quantity || 0 },
      { key: 'totalOrders', label: 'Orders', align: 'center', render: (row) => count(row.totalOrders || row.orders || row.orderCount), export: (row) => row.totalOrders || row.orders || row.orderCount || 0 },
      { key: 'totalSales', label: 'Total Sales', align: 'right', render: (row) => money(row.totalSales || row.sales || row.revenue), export: (row) => row.totalSales || row.sales || row.revenue || 0 },
      { key: 'action', label: 'Action', align: 'center', render: (row) => <ViewButton onClick={() => openDetails('products', row)} /> },
    ],
    customers: [
      { key: 'sno', label: 'S.No', align: 'center' },
      { key: 'name', label: 'Customer Name', render: customerName, export: customerName },
      { key: 'phone', label: 'Phone', render: customerPhone, export: customerPhone },
      { key: 'totalOrders', label: 'Total Orders', align: 'center', render: (row) => count(row.totalOrders || row.orders || row.orderCount), export: (row) => row.totalOrders || row.orders || row.orderCount || 0 },
      { key: 'totalProductsPurchased', label: 'Products Purchased', align: 'center', render: (row) => count(row.totalProductsPurchased || row.productsPurchased || row.totalQuantity), export: (row) => row.totalProductsPurchased || row.productsPurchased || row.totalQuantity || 0 },
      { key: 'totalSpent', label: 'Total Spent', align: 'right', render: (row) => money(row.totalSpent || row.spent || row.finalAmount), export: (row) => row.totalSpent || row.spent || row.finalAmount || 0 },
      { key: 'lastOrderDate', label: 'Last Order', render: (row) => customerDateText(row.lastOrderDate || row.orderDate || row.createdAt), export: (row) => exportDateTime(row.lastOrderDate || row.orderDate || row.createdAt) },
      { key: 'action', label: 'Action', align: 'center', render: (row) => <ViewButton onClick={() => openDetails('customers', row)} /> },
    ],
    orders: [
      { key: 'sno', label: 'S.No', align: 'center' },
      { key: 'orderId', label: 'Order ID', render: orderId, export: (row) => orderId(row) },
      { key: 'customer', label: 'Customer', render: customerName, export: customerName },
      { key: 'orderDate', label: 'Order Date', render: (row) => dateText(row.orderDate || row.createdAt), export: (row) => exportDateTime(row.orderDate || row.createdAt) },
      { key: 'items', label: 'Items', align: 'center', render: (row) => count(row.totalItems || row.itemsCount || (Array.isArray(row.products) ? row.products.length : 0)), export: (row) => row.totalItems || row.itemsCount || (Array.isArray(row.products) ? row.products.length : 0) },
      { key: 'quantity', label: 'Quantity', align: 'center', render: (row) => count(row.totalQuantity || row.quantity || row.productsSold), export: (row) => row.totalQuantity || row.quantity || row.productsSold || 0 },
      { key: 'productsTotal', label: 'Products Total', align: 'right', render: (row) => money(row.productsTotal || row.subtotal || row.totalAmount), export: (row) => row.productsTotal || row.subtotal || row.totalAmount || 0 },
      { key: 'discount', label: 'Discount', align: 'right', render: (row) => money(discountValue(row)), export: (row) => discountValue(row) },
      { key: 'finalAmount', label: 'Final Amount', align: 'right', render: (row) => money(row.finalAmount || row.total || row.amount), export: (row) => row.finalAmount || row.total || row.amount || 0 },
      { key: 'payment', label: 'Payment', render: (row) => text(row.paymentMethod || row.paymentStatus || row.payment), export: (row) => text(row.paymentMethod || row.paymentStatus || row.payment) },
      { key: 'status', label: 'Status', align: 'center', render: (row) => statusBadge(row.orderStatus || row.status), export: (row) => text(row.orderStatus || row.status) },
      { key: 'action', label: 'Action', align: 'center', render: (row) => <ViewButton onClick={() => openDetails('orders', row)} /> },
    ],
    payments: [
      { key: 'sno', label: 'S.No', align: 'center' },
      { key: 'paymentId', label: 'Payment ID', render: paymentId, export: (row) => paymentId(row) },
      { key: 'customer', label: 'Customer', render: customerName, export: customerName },
      { key: 'orderId', label: 'Order ID', render: paymentOrderId, export: (row) => paymentOrderId(row) },
      { key: 'products', label: 'Products', render: productsText, export: productsText },
      { key: 'paymentMethod', label: 'Payment Method', render: (row) => text(row.paymentMethod || row.method), export: (row) => text(row.paymentMethod || row.method) },
      { key: 'paymentStatus', label: 'Payment Status', align: 'center', render: (row) => statusBadge(row.paymentStatus || row.status), export: (row) => text(row.paymentStatus || row.status) },
      { key: 'amount', label: 'Amount', align: 'right', render: (row) => money(row.paymentAmount || row.amount || row.finalAmount), export: (row) => row.paymentAmount || row.amount || row.finalAmount || 0 },
      { key: 'paymentDate', label: 'Payment Date', render: (row) => dateText(row.paymentDate || row.createdAt || row.orderDate), export: (row) => exportDateTime(row.paymentDate || row.createdAt || row.orderDate) },
      { key: 'action', label: 'Action', align: 'center', render: (row) => <ViewButton onClick={() => openDetails('payments', row)} /> },
    ],
  };
  const columns = columnsByReport[activeReport];
  const searchTerm = String(activeFilter.search || '').trim().toLowerCase();
  const rows = searchTerm ? reportData[activeReport].filter((row) => {
    const haystack = searchableText({
      ...row,
      orderId: activeReport === 'payments' ? paymentOrderId(row) : orderId(row),
      orderNumber: orderNumber(row),
      paymentId: paymentId(row),
      customerName: customerName(row),
      couponCode: directValue(row, ['couponCode', 'couponCodeValue', 'coupon']) || (row && row.coupon && (row.coupon.code || row.coupon.couponCode)),
      productName: productName(row),
      products: productsText(row),
      transactionId: directValue(row, ['transactionId', 'transactionID', 'razorpayPaymentId', 'txnId', 'gatewayTransactionId']),
    }).toLowerCase();
    return haystack.includes(searchTerm);
  }) : reportData[activeReport];
  const currentPage = meta[activeReport].page || pages[activeReport];
  const totalRecords = meta[activeReport].total || 0;
  const stats = useMemo(() => getReportStats(activeReport, rows), [activeReport, rows]);

  return (
    <AdminLayout>
      <div className="reports-page rp-clean-page">
        <h1 className="rp-page-title">Reports</h1>

        <div className="rp-tabs">
          {REPORTS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                type="button"
                key={item.key}
                className={activeReport === item.key ? 'is-active' : ''}
                onClick={() => {
                  setActiveReport(item.key);
                  setDateOpen(false);
                }}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        <div className="rp-filter-shell">
          <SearchField value={activeFilter.search} onChange={(value) => setDraft({ search: value })} />
          <div className="rp-date-wrap">
            <button
              type="button"
              className={'rp-date-button ' + (activeFilter.fromDate || activeFilter.toDate ? 'is-active' : '')}
              onClick={() => setDateOpen(true)}
            >
              <CalendarDays size={17} />
              <span>{dateLabel()}</span>
            </button>
          </div>
          <div className="rp-actions">
            <button type="button" className="rp-btn rp-btn-ghost" onClick={resetFilters}>Reset</button>
            <button type="button" className="rp-btn rp-btn-gold" disabled={exporting} onClick={exportCurrent}>
              <Download size={16} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>

        {/* Statistic cards for currently selected report */}
        <ReportStatsCards stats={stats} onSelectReport={(key) => { setActiveReport(key); setDateOpen(false); }} />

        <section className="rp-card">
          <div className="rp-card-hdr">
            <div>
              <h3 className="rp-card-title">{config.label}</h3>
              <p className="rp-card-sub">Detailed report data from the selected section</p>
            </div>
            <span className="rp-records">Total Records: {count(totalRecords)}</span>
          </div>
          <DataTable section={activeReport} columns={columns} rows={rows} loading={loading} page={currentPage} />
          <Pagination page={currentPage} total={totalRecords} onChange={(page) => setPages((prev) => ({ ...prev, [activeReport]: page }))} />
        </section>
      </div>

      {dateOpen && (
        <DateFilterPopup
          value={activeFilter}
          onApply={applyDate}
          onCancel={() => setDateOpen(false)}
          onExport={exportCurrent}
          rowsCount={rows.length}
        />
      )}
      <DetailModal modal={detailModal} onClose={() => setDetailModal(null)} />
    </AdminLayout>
  );
}

export default Reports;
